import {
  ROUTE_MINI_QUIZ_FOOD_PREFERENCES_INTRO,
  ROUTE_QUIZ_FOOD_PREFERENCES_INTRO,
} from '@hungryroot/routes';
import { type QuizStorageNamespace } from './localStorage';

/**
 * `<funnel>/food-preferences-intro/?from=<source>` — how a redirect that
 * drops a guest onto the intro from outside the quiz marks where they came
 * from:
 *
 *   - `login` — the post-login redirect for a guest with unfinished
 *     explicit selections (`resolveFreemiumPostLoginRoute`)
 *   - `cta`   — a funnel CTA that found the same thing
 *     (`enterMiniQuizTailAsync`, `enterFullQuizAsGuestAsync`)
 *
 * The intro reads the marker to drop the "Next," that only makes sense when
 * the screen follows a quiz step. It is intentionally not carried past the
 * intro: Continue pushes a bare pathname.
 */
const FOOD_PREFERENCES_INTRO_FROM_PARAM = 'from';

const FOOD_PREFERENCES_INTRO_ENTRY_SOURCES = ['login', 'cta'] as const;

export type FoodPreferencesIntroEntrySource =
  (typeof FOOD_PREFERENCES_INTRO_ENTRY_SOURCES)[number];

const FOOD_PREFERENCES_INTRO_ROUTES = {
  quiz: ROUTE_QUIZ_FOOD_PREFERENCES_INTRO,
  'mini-quiz': ROUTE_MINI_QUIZ_FOOD_PREFERENCES_INTRO,
} as const satisfies Record<QuizStorageNamespace, string>;

/** The funnel's intro screen, marked with the entry source. */
export function getFoodPreferencesIntroEntryRoute({
  funnel,
  source,
}: {
  funnel: QuizStorageNamespace;
  source: FoodPreferencesIntroEntrySource;
}): string {
  return `${FOOD_PREFERENCES_INTRO_ROUTES[funnel]}?${FOOD_PREFERENCES_INTRO_FROM_PARAM}=${source}`;
}

function isFoodPreferencesIntroEntrySource(
  value: string | null
): value is FoodPreferencesIntroEntrySource {
  return FOOD_PREFERENCES_INTRO_ENTRY_SOURCES.some(
    (source) => source === value
  );
}

/**
 * Whether a location's `search` string marks an entry from outside the quiz
 * (login or a CTA), as opposed to arriving from the preceding quiz step.
 */
export function isFoodPreferencesIntroDirectEntry(search: string): boolean {
  return isFoodPreferencesIntroEntrySource(
    new URLSearchParams(search).get(FOOD_PREFERENCES_INTRO_FROM_PARAM)
  );
}
